import type { LocalStorageTokenData } from '../types'
import type { FederatedSession } from './wapi-client'

export interface FederatedLoginDeps {
    provider: 'google'
    /** The provider SDK credential/token to exchange (Google JWT). */
    providerToken: string
    method?: string
    interaction?: string
    /** true for silent auto-login, false for an explicit user action. */
    auto: boolean
    email?: string | null
    /** Exchange the provider credential for a WAPI session (federatedLogin, bound to urlRoot + creds). */
    exchange: (provider: 'google', token: string) => Promise<FederatedSession>
    writeMirror: (entry: LocalStorageTokenData) => void
    setSnowplowUserId: (id: string | null) => void
    setRumUser: (user: { id?: string; email?: string }) => void
    trackLoginEvent: (opts: {
        provider: string
        method?: string
        interaction?: string
        email?: string | null
        success: boolean
        auto: boolean
    }) => void
    identify: (data: Record<string, unknown>) => Promise<void>
}

/**
 * Orchestrate a federated login (ui-react parity): exchange the provider credential for a
 * WAPI session, write the cross-app mirror (so the navbar flips + ui-react/canopy see it),
 * set the analytics identity (Snowplow `setUserId` + DD RUM `setUser`), fire the
 * `user_accounts` login event, and POST `user:action:identify`. The identify call is
 * best-effort — its failure must not fail the login. All collaborators are injected.
 */
export async function performFederatedLogin(deps: FederatedLoginDeps): Promise<void> {
    const session = await deps.exchange(deps.provider, deps.providerToken)

    deps.writeMirror({
        authToken: session.token,
        authTokenExp: session.expiresAt,
        email: deps.email ?? null,
        identityId: session.identityId,
        provider: deps.provider,
        sessionId: session.sessionId,
        // ui-react stores the provider credential as accessToken too — keep the shape identical.
        accessToken: deps.providerToken,
    })

    deps.setSnowplowUserId(session.identityId)
    deps.setRumUser({
        ...(session.identityId ? { id: session.identityId } : {}),
        ...(deps.email ? { email: deps.email } : {}),
    })
    deps.trackLoginEvent({
        provider: deps.provider,
        method: deps.method,
        interaction: deps.interaction,
        email: deps.email,
        success: true,
        auto: deps.auto,
    })

    try {
        await deps.identify({
            cognito_identity_id: session.identityId,
            email: deps.email ?? null,
            source: 'account_login',
        })
    } catch {
        // best-effort: identify is fire-and-forget; a failure must not fail the login
    }
}
